import { calculateResult } from "pages/lenderReporting/drawer/field-management/calculated-field";

export const getTotalNoiValue = (items) =>
  items.reduce((total, lineItem) => {
    switch (lineItem.operator) {
      case "addition":
        return total + lineItem.amount;
      case "subtraction":
        return total - lineItem.amount;
      default:
        return total;
    }
  }, 0);

export const updateSortOrder = (lineItems) => {
  const incomeLineItems = lineItems.filter((li) => li.type === "income").sort((a, b) => a.sortOrder - b.sortOrder);
  const expenseLineItems = lineItems.filter((li) => li.type === "expense").sort((a, b) => a.sortOrder - b.sortOrder);

  for (let i = 0; i < incomeLineItems.length; i++) {
    incomeLineItems[i].sortOrder = i;
    incomeLineItems[i].id = `income${i}`;
  }

  for (let i = 0; i < expenseLineItems.length; i++) {
    expenseLineItems[i].sortOrder = i;
    expenseLineItems[i].id = `expense${i}`;
  }
};

const updateNoiField = (field, totalIncome, totalExpense, allFields, noiFieldUsed) => {
  // update noiField
  if (field && field.isFromLoanBoss && field.reportGroupId) {
    field.value = field.noiType === "income" ? totalIncome : totalExpense;
    field.formattedValue = (field.noiType === "income" ? totalIncome : totalExpense).toString();
    return true;
  }
  // update calculatedField with noiField used
  if (field && field.isCalculated) {
    for (const calculation of field.calculations) {
      if (calculation.fieldId) {
        const fieldRef = allFields.find((f) => f.id === calculation.fieldId);
        if (fieldRef && fieldRef.isFromLoanBoss && fieldRef.reportGroupId) {
          calculation.value = fieldRef.noiType === "income" ? totalIncome : totalExpense;
        }
      }
    }
    // update calculatedField value
    field.value = calculateResult(field.calculations);
    field.formattedValue = field.value.toString();
    return true;
  }

  return noiFieldUsed;
};

const updateMultiPropComparisonLineItemTotal = (lineItem) => {
  let total = 0;

  const { comparisonOperator, comparisonCalculationType } = lineItem;
  const comparisonGroups = lineItem.multiplePropertiesComparisonFields;

  if (comparisonCalculationType === "cumulative") {
    const calculateGroupTotal = (group) => {
      let groupTotal = 0;
      for (const field of group) {
        if (field.selectedField?.value || field.selectedField?.value === 0) {
          groupTotal += field.selectedField.value;
        }
      }
      return groupTotal;
    };

    const cumulativeValuesPerGroup = comparisonGroups.map((group) => calculateGroupTotal(group));

    total = comparisonOperator === "greaterOf" ? Math.max(...cumulativeValuesPerGroup) : Math.min(...cumulativeValuesPerGroup);
    const indexOfGroupUsed = cumulativeValuesPerGroup.indexOf(total);
    lineItem.selectedGroupIndex = indexOfGroupUsed;
  } else {
    const selectedValuesPerProperty = [];
    for (let i = 0; i < comparisonGroups[0].length; i++) {
      const valuesPerProperty = [];
      for (const group of comparisonGroups) {
        if (group[i].selectedField?.value || group[i].selectedField?.value === 0) {
          valuesPerProperty.push(group[i].selectedField.value);
        }
      }

      if (valuesPerProperty.length > 0) {
        const selectedValue = comparisonOperator === "greaterOf" ? Math.max(...valuesPerProperty) : Math.min(...valuesPerProperty);
        selectedValuesPerProperty.push(selectedValue);
      }
    }

    total = selectedValuesPerProperty.reduce((acc, curr) => {
      if (curr || curr === 0) {
        return acc + curr;
      }
      return acc;
    }, 0);
  }

  lineItem.amount = total;
};

const updateSinglePropComparisonLineItemTotal = (lineItem) => {
  const { comparisonOperator, comparisonFields } = lineItem;

  const fieldValues = comparisonFields && comparisonFields.map((field) => field.value);

  lineItem.amount = comparisonOperator === "greaterOf" ? Math.max(...fieldValues) : Math.min(...fieldValues);
};

const updateLineItemsWithNoiField = (lineItem) => {
  if (lineItem.isMultipleProperties) {
    if (!lineItem.isComparison) {
      const fieldsPerProperty = lineItem.multiplePropertiesValueFields;
      let updatedAmount = 0;
      for (const propertyField of fieldsPerProperty) {
        const field = propertyField.selectedField;
        if (field) {
          updatedAmount += field.value;
        }
      }
      lineItem.amount = updatedAmount;
    } else {
      updateMultiPropComparisonLineItemTotal(lineItem);
    }
  } else {
    if (!lineItem.isComparison) {
      lineItem.amount = lineItem.field.value || 0;
    } else {
      updateSinglePropComparisonLineItemTotal(lineItem);
    }
  }
};

export const updateLineItemsIfNoiFieldIsUsed = (lineItems, allFields) => {
  const totalIncome = getTotalNoiValue(lineItems.filter((li) => li.type === "income"));
  const totalExpense = getTotalNoiValue(lineItems.filter((li) => li.type === "expense"));

  for (const lineItem of lineItems) {
    let noiFieldUsed = false;
    if (lineItem.isMultipleProperties) {
      if (!lineItem.isComparison) {
        const fieldsPerProperty = lineItem.multiplePropertiesValueFields;
        for (const propertyField of fieldsPerProperty) {
          const field = propertyField.selectedField;
          noiFieldUsed = updateNoiField(field, totalIncome, totalExpense, allFields, noiFieldUsed);
        }
      } else {
        const multiplePropertiesComparisonFields = lineItem.multiplePropertiesComparisonFields;
        for (const comparisonGroup of multiplePropertiesComparisonFields) {
          for (const propertyField of comparisonGroup) {
            if (propertyField.selectedField) {
              noiFieldUsed = updateNoiField(propertyField.selectedField, totalIncome, totalExpense, allFields, noiFieldUsed);
            }
          }
        }
      }
    } else {
      if (!lineItem.isComparison) {
        noiFieldUsed = updateNoiField(lineItem.field, totalIncome, totalExpense, allFields, noiFieldUsed);
      } else {
        const comparisonFields = lineItem.comparisonFields;
        for (const field of comparisonFields) {
          noiFieldUsed = updateNoiField(field, totalIncome, totalExpense, allFields, noiFieldUsed);
        }
      }
    }

    if (noiFieldUsed) {
      updateLineItemsWithNoiField(lineItem);
    }
  }

  return lineItems;
};
